import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import * as Sentry from "@sentry/react";
import Parser from "html-react-parser";
import TextLink from "../library/TextLink";
import { RichTextField } from "../library/Form";
import { RichTextElementStyles } from "../library/RichTextEditor/RichTextEditor";
import SubmitButton from "../library/Form/SubmitButton";
import Employer from "../../apiResources/Employer";
import EmployerUser from "../../apiResources/EmployerUser";
import SettingLabel from "./SettingLabel";

const CompanyDescriptionSettingsOption = (props: Props) => {
  const {
    user,
    company,
    isEditing = false,
    disabled = false,
    onEditStart,
    onEditComplete,
    noCancel,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    if (values.profile === company.profile) {
      onEditComplete?.();
      return;
    }

    try {
      company.assignAttributes(values);
      const success = await company.save();
      if (success) {
        onEditComplete();
      } else {
        formik.setErrors({
          profile: company.errors.profile?.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      company.rollback();
    }
  };

  const edit = () => {
    if (!disabled) {
      onEditStart?.();
    }
  };

  const value = company?.profile;

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Company Description</SettingLabel>
          {user.isAdmin && (
            <TextLink disabled={disabled} onClick={edit}>
              Edit
            </TextLink>
          )}
        </Header>
        {!value && <div>Not specified</div>}
        {value && <DescriptionDisplay>{Parser(value)}</DescriptionDisplay>}
      </section>
    );
  }

  const initialValues: FormValues = {
    profile: company.profile,
  };

  const validation = Yup.object({
    profile: Yup.string().required("Please enter your profile"),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Company Description</SettingLabel>
            {!noCancel && <TextLink onClick={onEditComplete}>Cancel</TextLink>}
          </Header>
          <RichTextField
            name="profile"
            label="Company Description"
            hiddenLabel
          />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type Props = {
  user: EmployerUser;
  company: Employer;
  isEditing?: boolean;
  disabled?: boolean;
  onEditStart?: () => void;
  onEditComplete?: () => void;
  noCancel?: boolean;
};

type FormValues = {
  profile: string;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const DescriptionDisplay = styled.div`
  ${RichTextElementStyles}
`;

export default CompanyDescriptionSettingsOption;
